/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import { ComponentManagerFn } from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { PivotColumnDefaultProps } from '../PivotTableStringColumn/PivotTableStringColumnManager';
import { defaultValue } from './PivotTableNumberColumn';

const PivotTableNumberColumnManager: ComponentManagerFn<
  'PivotTableNumberColumn'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'PivotTableNumberColumn'>(
      platform,
      'PivotTableNumberColumn',
    ),
    type: 'PivotTableNumberColumn',
    name: 'Pivot Table Number Column',
    isChildAllowed: (page, item, childType) =>
      ([] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...PivotColumnDefaultProps,
      viewAttribute: {
        ...PivotColumnDefaultProps.viewAttribute,
        getAttributeDataTypes: () => [
          'Integer',
          'Double',
          'Percent',
          'Decimal',
        ],
      },
      showNegativeInBrackets: {
        label: 'Show Negative In Brackets',
        type: 'boolean',
        helpText: 'Enable this property to show negative values in brackets.',
      },
    },
    getDisplayText: (item) => item.props.viewAttribute,
    category: 'Pivot Table',
    icon: { iconName: 'io-number-input', iconType: 'cloudio' },
    description: `**Overview**<br/>
The Pivot Table Number Column is a specialized column component designed for Pivot Tables to display numeric data. It is optimized for rendering quantitative fields such as integers, decimals, percentages, and doubles, and includes specific formatting options for financial or mathematical data.
Use this component whenever you need to present countable or measurable data—like sales figures, quantities, or scores—within a pivot grid.

**1. Data Connection**<br/>
* **View Attribute:** Connects the column to a specific field in your data source. This component strictly accepts numeric data types to ensure proper alignment and mathematical consistency:
    * **Integer:** For whole numbers (e.g., "Quantity").
    * **Double:** For floating-point numbers or scientific data.
    * **Decimal:** For high-precision numbers, such as currency or tax rates.
    * **Percent:** For fractional values that need to be formatted as percentages (e.g., "Discount Rate").

**2. Formatting & Presentation**<br/>
* **Show Negative In Brackets:** A boolean property specifically tailored for professional financial reporting.
    * **Enabled (True):** Negative numbers are displayed in parentheses, e.g., **(150.00)**. This is the standard convention in accounting to denote losses, credits, or deductions.
    * **Disabled (False):** Negative numbers use the standard minus sign, e.g., **-150.00**.

**3. Usage Context**<br/>
* **Strictly Display:** Unlike the Date or String columns, this component does not support child components (like TriggerOnClick). It is designed as a high-performance, read-only display element for numeric values.
* **Inheritance:** It inherits standard layout properties from the common **PivotColumnDefaultProps**, allowing you to configure **Header Text**, **Column Width**, and **Horizontal Alignment** (typically right-aligned for numbers).

**4. Best Use Cases**<br/>
* **Financial Reports:** Displaying Revenue, Cost, and Net Income columns where negative values must be clearly distinguished using accounting brackets.
* **Inventory Management:** Showing "Quantity on Hand," "Reorder Levels," or "Unit Price."
* **Performance Dashboards:** Presenting Key Performance Indicators (KPIs) such as averages, calculated scores, or growth percentages.
* **Scientific Data:** Reporting precise measurements or high-precision decimal readings from IoT sensors.`,
  };
};

export default PivotTableNumberColumnManager;
